import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { InternalOpsPage, InternalOpsState, PageControls, SortableTable, TABLE_PAGE_SIZE, rowsForPage, useInternalOpsFetch, withQuery } from "./internalOpsUi";
import { API_ENDPOINTS } from "../../config/apiConfig";

function formatCount(value) {
  if (value == null) return "—";
  return value.toLocaleString?.() ?? String(value);
}

function formatCell(value) {
  if (value == null || value === "") return "—";
  if (value === true) return "Yes";
  if (value === false) return "No";
  return String(value);
}

export default function InternalOpsDatabase() {
  const { data, loading, error } = useInternalOpsFetch(API_ENDPOINTS.internalOpsDatabase);

  return (
    <InternalOpsPage
      title="Database"
      subtitle="Each product app has its own page. Open a table to see every column and page through its rows."
    >
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {data.app_count} apps · {data.table_count} tables
            </p>
            <SortableTable
              rows={data.apps}
              empty="No applications found."
              rowKey={(app) => app.app}
              columns={[
                {
                  key: "title",
                  label: "Application",
                  sortValue: (app) => app.title,
                  render: (app) => (
                    <Link
                      to={`/internal/database/${encodeURIComponent(app.app)}`}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {app.title}
                    </Link>
                  ),
                },
                { key: "table_count", label: "Tables", sortValue: (app) => app.table_count, render: (app) => formatCount(app.table_count) },
                { key: "row_count", label: "Rows", sortValue: (app) => app.row_count, render: (app) => formatCount(app.row_count) },
              ]}
            />
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}

export function InternalOpsDatabaseApp() {
  const { appLabel } = useParams();
  const { data, loading, error } = useInternalOpsFetch(API_ENDPOINTS.internalOpsDatabaseApp(appLabel));

  return (
    <InternalOpsPage
      title={data?.title || "App"}
      subtitle={data ? `${data.app} · ${data.table_count} tables` : "Tables in this app."}
    >
      <p className="mb-4 text-sm">
        <Link to="/internal/database" className="font-medium text-blue-700 hover:underline">
          All apps
        </Link>
      </p>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <SortableTable
            rows={data.tables}
            rowKey={(row) => row.model}
            columns={[
              {
                key: "title",
                label: "Table",
                sortValue: (row) => row.title,
                render: (row) => (
                  <>
                    <Link
                      to={`/internal/database/${encodeURIComponent(data.app)}/${encodeURIComponent(row.model)}`}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {row.title}
                    </Link>
                    <p className="mt-1 font-mono text-xs text-slate-500">{row.model}</p>
                  </>
                ),
              },
              { key: "db_table", label: "Database name", cellClassName: "font-mono text-xs", sortValue: (row) => row.db_table, render: (row) => row.db_table },
              { key: "column_count", label: "Columns", sortValue: (row) => row.column_count, render: (row) => row.column_count },
              { key: "count", label: "Rows", sortValue: (row) => row.count, render: (row) => formatCount(row.count) },
            ]}
          />
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}

export function InternalOpsDatabaseTable() {
  const { appLabel, modelName } = useParams();
  const scope = `${appLabel}/${modelName}`;
  const [paging, setPaging] = useState({ scope, page: 1 });
  if (paging.scope !== scope) {
    setPaging({ scope, page: 1 });
  }
  const page = paging.scope === scope ? paging.page : 1;
  const setPage = (next) => setPaging({ scope, page: next });
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsDatabaseTable(appLabel, modelName), { page, page_size: TABLE_PAGE_SIZE })
  );
  const columns = data?.columns || [];

  return (
    <InternalOpsPage
      title={data?.title || "Table"}
      subtitle={
        data
          ? `${data.db_table} · ${formatCount(data.count)} rows`
          : "Every column, with rows split across pages."
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        <Link to="/internal/database" className="font-medium text-blue-700 hover:underline">
          All apps
        </Link>
        <span className="px-2 text-slate-300">/</span>
        <Link
          to={`/internal/database/${encodeURIComponent(appLabel)}`}
          className="font-medium text-blue-700 hover:underline"
        >
          {data?.app_title || appLabel}
        </Link>
      </p>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="space-y-8">
            <section>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">
                All columns ({columns.length})
              </h2>
              <SortableTable
                rows={columns}
                rowKey={(column) => column.name}
                columns={[
                  { key: "title", label: "Title", sortValue: (column) => column.title, render: (column) => column.title },
                  { key: "name", label: "Variable", cellClassName: "font-mono text-xs", sortValue: (column) => column.name, render: (column) => column.name },
                  { key: "type", label: "Type", cellClassName: "text-slate-600", sortValue: (column) => column.type, render: (column) => column.type },
                ]}
              />
            </section>

            <section className="min-w-0 space-y-3">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Rows</h2>
              <PageControls
                page={data.page || page}
                pageCount={data.page_count}
                count={data.count}
                pageSize={data.page_size || TABLE_PAGE_SIZE}
                onPage={setPage}
              />
              {data.rows?.length ? (
                <SortableTable
                  paginate={false}
                  rows={rowsForPage(data.rows, data.page || page, data.page_size || TABLE_PAGE_SIZE)}
                  rowKey={(row, index) => row.id ?? `${page}-${index}`}
                  columns={columns.map((column) => ({
                    key: column.name,
                    label: column.title,
                    sublabel: column.name,
                    cellClassName: "max-w-xs truncate align-top text-slate-800",
                    sortValue: (row) => row[column.name],
                    cellTitle: (row) => formatCell(row[column.name]),
                    render: (row) => formatCell(row[column.name]),
                  }))}
                />
              ) : (
                <p className="text-sm text-slate-500">This table has no rows.</p>
              )}
            </section>
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}
